import React, { useEffect, useRef, useState } from 'react';
import { limpiarNumero, aNumero, MONTO_MAXIMO } from '../utils/validacion';

// ---------------------------------------------------------------------------
// Campo para escribir NÚMEROS (dinero, cantidades, porcentajes).
//
// Reemplaza a <input type="number">, que deja escribir letras como "e",
// ignora min/max y se cambia solo al pasar la ruedita del mouse.
//
// Qué hace este campo:
//  · No deja escribir letras ni símbolos — y avisa: "Solo números aquí".
//  · Entiende "1,500" y lo guarda como 1500 (coma de miles) y "10,50" → 10.50.
//  · No deja pasarse del máximo (ej. 100% de descuento) y avisa por qué.
//  · Si escribes menos del mínimo, lo corrige al salir y te lo dice.
//  · La ruedita del mouse ya no cambia el monto sin querer.
//  · En el celular abre el teclado numérico.
//
// Uso:
//   <CampoNumero valor={monto} onChange={setMonto} prefijo="L" min={0} />
//   <CampoNumero valor={cant} onChange={setCant} decimales={0} min={1} max={stock}
//                maxMensaje={`Solo hay ${stock} en existencia`} />
// ---------------------------------------------------------------------------
export default function CampoNumero({
  valor,
  onChange,
  decimales = 2,          // 0 = entero (cantidades, stock)
  negativos = false,      // true solo en saldos de tarjeta
  min,
  max,
  maxMensaje,             // mensaje a la medida al pasarse del máximo
  minMensaje,
  minSiVacio = false,     // si lo dejan vacío, se rellena con el mínimo
                          // (para cantidades: nunca debe quedar en blanco)
  etiqueta,               // opcional: pone el <label> arriba
  prefijo,                // 'L' para dinero
  sufijo,                 // '%' para porcentajes
  ayuda,                  // texto de ayuda fijo, debajo del campo
  error,                  // error que manda el formulario (ej. al guardar)
  estilo = {},            // estilos propios de cada pantalla
  deshabilitado = false,
  placeholder,
  autoFocus,
  ...resto
}) {
  const [aviso, setAviso] = useState('');
  const temporizador = useRef(null);

  useEffect(() => () => clearTimeout(temporizador.current), []);

  // Muestra el mensajito unos segundos y lo desvanece.
  const avisar = (texto) => {
    setAviso(texto);
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setAviso(''), 3500);
  };

  const topeReal = max === undefined ? (decimales === 0 ? 999999 : MONTO_MAXIMO) : max;

  const escribir = (e) => {
    const { valor: limpio, rechazado } = limpiarNumero(e.target.value, { decimales, negativos });

    if (rechazado) {
      avisar(decimales === 0
        ? 'Solo se pueden escribir números enteros en esta casilla'
        : 'Solo se pueden escribir números en esta casilla');
    }

    // No se puede pasar del máximo: no se acepta la tecla y se explica.
    const n = aNumero(limpio);
    if (n !== null && n > topeReal) {
      avisar(maxMensaje || (max !== undefined
        ? `No puede ser mayor que ${max}${sufijo === '%' ? '%' : ''}`
        : 'Ese número es demasiado grande'));
      return;
    }

    onChange(limpio);
  };

  // Al salir del campo: completar el mínimo y limpiar puntos sueltos ("5." → "5").
  const salir = (e) => {
    let t = String(valor ?? '');
    if (t.endsWith('.')) t = t.slice(0, -1);
    // Una cantidad nunca debe quedar en blanco: se repone el mínimo.
    if (t === '' && minSiVacio && min !== undefined) t = String(min);
    if (t !== '' && min !== undefined) {
      const n = aNumero(t);
      if (n !== null && n < min) {
        avisar(minMensaje || (min === 0 ? 'No puede ser negativo, lo dejé en 0' : `El mínimo es ${min}`));
        t = String(min);
      }
    }
    if (t !== String(valor ?? '')) onChange(t);
    if (resto.onBlur) resto.onBlur(e);
  };

  const hayProblema = !!error;
  const campo = {
    width: '100%',
    padding: '10px',
    border: `1px solid ${hayProblema ? '#EF4444' : '#D1D5DB'}`,
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    ...estilo,
    ...(hayProblema ? { border: '1px solid #EF4444' } : null),
    ...(prefijo ? { paddingLeft: '28px' } : null),
    ...(sufijo ? { paddingRight: '30px' } : null),
  };

  const adorno = (lado) => ({
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', [lado]: '10px',
    fontSize: '13px', fontWeight: 600, color: '#9CA3AF', pointerEvents: 'none',
  });

  return (
    <div style={{ width: estilo.width || '100%' }}>
      {etiqueta && (
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
          {etiqueta}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {prefijo && <span style={adorno('left')}>{prefijo}</span>}
        <input
          {...resto}
          type="text"
          inputMode={decimales === 0 ? 'numeric' : 'decimal'}
          autoComplete="off"
          value={valor ?? ''}
          disabled={deshabilitado}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={escribir}
          onBlur={salir}
          // La ruedita del mouse ya no cambia el número sin querer.
          onWheel={(e) => e.currentTarget.blur()}
          style={campo}
        />
        {sufijo && <span style={adorno('right')}>{sufijo}</span>}
      </div>
      {(error || aviso) && (
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: error ? '#EF4444' : '#B45309', lineHeight: 1.35 }}>
          ⚠ {error || aviso}
        </p>
      )}
      {!error && !aviso && ayuda && (
        <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF', lineHeight: 1.35 }}>{ayuda}</p>
      )}
    </div>
  );
}
